import React from 'react';
import { connect } from 'react-redux';

import PropTypes from 'prop-types';

import { getCheque } from 'apps/auth/actions/historyAction';
import { Badge } from 'apps/auth/components/common/Badge/Badge';
import { DiskBadge } from 'apps/auth/components/common/DiskBadge/DiskBadge';
import { Icon } from 'apps/auth/components/common/Icon/Icon';
import { Row } from 'apps/auth/components/common/Layout/Row';
import { PanelRow } from 'apps/auth/components/common/Panel/styles';
import { Text } from 'apps/auth/components/common/Text/Text';
import {
    CATEGORY_TRANSFER_TO_CASH,
    CATEGORY_TRANSFER_TO_CASH_UNITELLER,
    CATEGORY_TRANSFER_TO_GLOBAL_ACCOUNT,
    CATEGORY_TRANSFER_TO_HOME_DELIVERY,
    CATEGORY_TRANSFER_TO_LIBRE,
    PS_BONUS,
    PS_BONUS_INCOMING,
    STATUS_OK,
} from 'apps/auth/constants/Constants';
import { logWarn } from 'apps/auth/helpers/clientLogger';
import { getIconForDiskBadge, getToAccount } from 'apps/auth/helpers/historyUtils';
import { resolveArguments, toLowerCase } from 'apps/auth/helpers/utils';
import { usePageMessages } from 'apps/auth/hooks/selectors';

import { TransactionsBlockSubheader, TransactionsFeedHeader, TransactionsFrame, TransactionsLineBottom, TransactionsLineBottomInfo } from '../styles';

const HistoryRecord = ({ record, addMonth, label, alreadyHasAwaitingBlock, ...props }) => {
    const { awaitingPayment, transferDefaultTitle, transferTopUpGAHeader, topupGATitle, ...messages } = usePageMessages();

    const activeTransactionOrDraft = record?.status < STATUS_OK || record.draft;
    const failedTransaction = record?.status > STATUS_OK && !record.draft;

    const showDetails = (e) => {
        e.preventDefault();
        props.getCheque(record.paymentId).catch((error) => {
            logWarn(error);
            return null;
        });
    };

    const getHeaderTitle = (showYear, monthNumber, yearNumber) => {
        if (record.awaitingPayment && alreadyHasAwaitingBlock) {
            return null;
        } else if (record.awaitingPayment) {
            return awaitingPayment;
        } else if (showYear) {
            return resolveArguments(messages['month_' + monthNumber + '_year'], yearNumber);
        } else {
            return messages['month_' + monthNumber];
        }
    };

    const getAvatar = (recordForAvatar) => {
        if (recordForAvatar.avatar) {
            return recordForAvatar.avatar;
        } else {
            return null;
        }
    };

    const getLetter = (recordForLetter) => {
        if (recordForLetter.avatar) {
            return null;
        } else if (recordForLetter.billId && recordForLetter.billId > 0) {
            return recordForLetter.firstLetter;
        } else {
            return null;
        }
    };

    const getAmountColor = (recordForColor) => {
        if (recordForColor.transactionType === PS_BONUS_INCOMING) {
            return 'primary';
        } else if (failedTransaction) {
            return 'lightGray';
        } else if (activeTransactionOrDraft) {
            return 'secondary';
        } else {
            return 'primary';
        }
    };

    const getAmountPrefix = (recordForPrefix) => {
        if (recordForPrefix.transactionType === PS_BONUS_INCOMING) {
            return '+';
        } else if (failedTransaction) {
            return '';
        } else {
            return '-';
        }
    };

    const getAmount = (recordForAmount) => {
        const currencyCode = recordForAmount.currencyCode && typeof recordForAmount.currencyCode === 'string' ? recordForAmount.currencyCode.toLowerCase() : '';
        return (
            <Text variant="div" color={getAmountColor(recordForAmount)} lineThrough={failedTransaction} flexShrink="0" ellipsis leftSpacer>
                {`${getAmountPrefix(recordForAmount)} ${recordForAmount.formatedAmount} `}
                <Text color={getAmountColor(recordForAmount)} smallCaps>
                    {currencyCode}
                </Text>
            </Text>
        );
    };

    const getHeader = (recordForTitle) => {
        if (recordForTitle.toProviderName === '' && recordForTitle.providerToCategory === CATEGORY_TRANSFER_TO_GLOBAL_ACCOUNT) {
            return transferTopUpGAHeader;
        }
        return recordForTitle.toProviderName === '' ? transferDefaultTitle : recordForTitle.toProviderName;
    };

    const getTitle = (recordForTitle) => {
        if (recordForTitle.billId !== 0) {
            return (
                <Text size="secondary" color={failedTransaction ? 'lightGray' : 'secondary'} ellipsis>
                    {recordForTitle.comment}
                </Text>
            );
        } else {
            let account =
                recordForTitle.providerToCategory === CATEGORY_TRANSFER_TO_CASH || recordForTitle.providerToCategory === CATEGORY_TRANSFER_TO_HOME_DELIVERY
                    ? ''
                    : recordForTitle.toAccount;
            if (recordForTitle.providerToCategory === CATEGORY_TRANSFER_TO_CASH_UNITELLER) {
                account = recordForTitle.toAccountBankName;
            }
            if (recordForTitle.providerToCategory === CATEGORY_TRANSFER_TO_GLOBAL_ACCOUNT) {
                account = resolveArguments(topupGATitle, recordForTitle.currencyCode);
            }
            return (
                <Text size="secondary" color={failedTransaction ? 'lightGray' : 'secondary'} ellipsis leftSpacer>
                    {getToAccount(recordForTitle.providerToCategory, recordForTitle?.toCountryId, account, messages)}
                </Text>
            );
        }
    };

    return (
        <li>
            {addMonth && (
                <TransactionsBlockSubheader isNewMainScreen={props.isNewMainScreen}>
                    <Text variant="div" color="secondary" size="subheader">
                        {getHeaderTitle(record.showYear, record.monthNumber, record.yearNumber)}
                    </Text>
                </TransactionsBlockSubheader>
            )}
            <TransactionsFeedHeader onClick={(e) => showDetails(e)} isNewMainScreen={props.isNewMainScreen}>
                <DiskBadge
                    iconName={getIconForDiskBadge(record, record.providerToCategory, record.transactionType)}
                    src={getAvatar(record)}
                    diskText={getLetter(record)}
                    variant={record.transactionType === PS_BONUS_INCOMING ? 'secondary' : 'tertiary'}
                    isDisabled={failedTransaction}
                    flagName={
                        record.transactionType === PS_BONUS_INCOMING || record.providerToCategory === CATEGORY_TRANSFER_TO_LIBRE
                            ? null
                            : toLowerCase(record.toCountryCode)
                    }
                />
                <TransactionsFrame>
                    <Row flex justify="between" indent="4px">
                        <Text color={failedTransaction ? 'lightGray' : 'primary'} ellipsis>
                            {getHeader(record)}
                        </Text>
                        {getAmount(record)}
                    </Row>
                    {record.transactionType !== PS_BONUS && record.transactionType !== PS_BONUS_INCOMING && (
                        <TransactionsLineBottom>
                            <TransactionsLineBottomInfo>{getTitle(record)}</TransactionsLineBottomInfo>
                            {record.status === STATUS_OK && <Icon name="check-rounded" size="16px" color="primary" leftIndent="8px" />}
                            {failedTransaction && <Icon name="cross-rounded" size="16px" color="lightGray" leftIndent="8px" />}
                            {activeTransactionOrDraft && !label && (
                                <Icon name={record.draft ? 'info-rounded' : 'clock-filled'} size="16px" color="secondary" leftIndent="8px" />
                            )}
                            {label && (
                                <PanelRow alignEnd>
                                    <Badge caption={label} />
                                </PanelRow>
                            )}
                        </TransactionsLineBottom>
                    )}
                </TransactionsFrame>
            </TransactionsFeedHeader>
        </li>
    );
};

HistoryRecord.propTypes = {
    record: PropTypes.shape({
        paymentId: PropTypes.number.isRequired,
        submitDate: PropTypes.oneOfType([
            PropTypes.string,
            PropTypes.shape({
                year: PropTypes.number.isRequired,
            }),
        ]).isRequired,
        transactionType: PropTypes.string.isRequired,
        toProviderName: PropTypes.string,
        toCardType: PropTypes.string,
        toAccount: PropTypes.string,
    }).isRequired,
    addMonth: PropTypes.bool,
    label: PropTypes.string,
};

export default connect(null, { getCheque })(HistoryRecord);
